const keyboardState = require('./keyboard-state.js')
const { normalize } = require('./matching.js')

const ANNOUNCE_DEBOUNCE_MS = 400

class Combobox {
  constructor($module) {
    this.$module = $module
    this.$input = $module.querySelector('.hse-combobox__input')
    this.$field = $module.querySelector('.hse-combobox__field')
    this.$clearButton = $module.querySelector('.hse-combobox__clear')
    this.$toggleButton = $module.querySelector('.hse-combobox__toggle')
    this.$listbox = $module.querySelector('.hse-combobox__listbox')
    this.$noResults = $module.querySelector('.hse-combobox__no-results')
    this.$status = $module.querySelector('[role="status"]')
    this.$error = $module.querySelector('.hse-error-message')
    this.$formGroup = $module.querySelector('.hse-form-group')
  }

  init() {
    if (!this.$input || !this.$listbox) {
      return
    }

    this.match = this.$module.dataset.match || 'start'
    this.minChars = Number(this.$module.dataset.minChars) || 1
    this.errorNotInList = this.$module.dataset.errorNotInList
    // Runtime announcement strings, translatable the same way as every other
    // combobox string (errorNotInList, noResultsText, ...) but read via data
    // attribute rather than rendered text, since each needs a value
    // interpolated at announce time, not fixed at page load.
    this.resultsOneText =
      this.$module.dataset.resultsOne || '%{count} result available'
    this.resultsOtherText =
      this.$module.dataset.resultsOther || '%{count} results available'
    this.selectedText = this.$module.dataset.selected || '%{option} selected'
    // A bare word, matching the server-rendered error-message component's
    // own visuallyHiddenText convention - the colon is appended wherever
    // this is actually used, not stored here.
    this.errorPrefix = this.$module.dataset.errorPrefix || 'Error'
    // Derived from which buttons the template rendered, rather than a
    // separate context attribute duplicating information the markup
    // already carries.
    this.context = this.$toggleButton
      ? 'set-list'
      : this.$module.querySelector('.hse-combobox__search')
        ? 'search'
        : 'address'

    this.$optionElements = Array.from(
      this.$listbox.querySelectorAll('.hse-combobox__option')
    )
    this.options = this.$optionElements.map(($option) => ({
      id: $option.id,
      label: $option.textContent.trim()
    }))

    this.state = keyboardState.createInitialState()
    this.announceTimer = null
    // The server-rendered error, if any - a form reset restores the value
    // that caused it, so it restores the error too.
    this.initialErrorHTML =
      this.$error && !this.$error.hidden ? this.$error.innerHTML : ''

    this.bindEvents()
    this.render()
  }

  bindEvents() {
    this.$input.addEventListener('input', this.handleInput.bind(this))
    this.$input.addEventListener('keydown', this.handleKeydown.bind(this))
    this.$input.addEventListener('blur', this.handleFocusLeave.bind(this))
    // The browser fires its own 'change' on blur whenever the value differs
    // from when the input gained focus - after a selection, that repeats
    // the 'change' applyResult already sent for the same value. Bound first,
    // so it runs before any consumer listener on the input.
    this.$input.addEventListener('change', (event) => {
      if (event.isTrusted && this.$input.value === this.lastChangeValue) {
        event.stopImmediatePropagation()
      }
    })

    if (this.$clearButton) {
      // Keeps focus in the input on a pointer press, as the toggle and
      // listbox do - so a Clear click never blurs the input and can't be
      // mistaken for leaving the field, in browsers that focus a clicked
      // button and in those (Safari) that don't. Keyboard activation of
      // Clear is unaffected.
      this.$clearButton.addEventListener('mousedown', (event) => {
        event.preventDefault()
      })
      this.$clearButton.addEventListener('click', this.handleClear.bind(this))
      // Tabbing on from Clear leaves the component just as tabbing on from
      // the input does - without this, the popup stays open over the next
      // field and the not-in-list check never runs.
      this.$clearButton.addEventListener(
        'blur',
        this.handleFocusLeave.bind(this)
      )
    }

    if (this.$toggleButton) {
      // Keeps focus in the input, as the listbox does below - otherwise the
      // input blurs on mousedown and the not-in-list error flashes (and is
      // announced) before the click re-opens the list.
      this.$toggleButton.addEventListener('mousedown', (event) => {
        event.preventDefault()
      })
      this.$toggleButton.addEventListener(
        'click',
        this.handleToggleClick.bind(this)
      )
    }

    // Keeps focus in the input through a mouse selection (the APG combobox
    // pattern) - without this, mousedown on an option would blur the input
    // before 'click' has a chance to confirm the selection, both breaking
    // the "focus never leaves the input" invariant and spuriously tripping
    // the not-in-list validation on that same blur.
    this.$listbox.addEventListener('mousedown', (event) => {
      event.preventDefault()
    })
    this.$listbox.addEventListener('click', this.handleOptionClick.bind(this))

    const $form = this.$input.closest('form')
    if ($form) {
      $form.addEventListener('submit', this.handleSubmit.bind(this))
      $form.addEventListener('reset', this.handleReset.bind(this))
    }

    window.addEventListener('resize', this.updatePosition.bind(this))
  }

  handleInput() {
    // Typed text is the user's own - the browser's 'change' on blur for it
    // is real, so stop treating it as a repeat of the last selection.
    this.lastChangeValue = null
    this.state = keyboardState.onType(this.state, {
      query: this.$input.value,
      options: this.options,
      mode: this.match,
      minChars: this.minChars
    })
    // Per the tech approach, an error is removed when an option is chosen
    // or the field is cleared - not on every keystroke, which would hide it
    // while the value is still invalid.
    if (this.$input.value.length === 0) {
      this.clearError()
    }
    this.render()
    this.announceResultCount()
  }

  handleKeydown(event) {
    // Modifier combinations (Cmd+ArrowDown moves the caret, for example) and
    // keys pressed while an IME is composing text belong to the browser.
    if (event.altKey || event.ctrlKey || event.metaKey || event.isComposing) {
      return
    }
    // A read-only or disabled value can't change - leave every key native
    // (no preventDefault), just closing a list left open from before it was
    // locked.
    if (this.isLocked()) {
      if (this.state.isOpen) {
        this.state = keyboardState.onEscape(this.state)
        this.render()
      }
      return
    }

    let nextState

    switch (event.key) {
      case 'ArrowDown':
        nextState =
          this.context === 'set-list' && this.state.matchCount === 0
            ? keyboardState.onRequestFullList(this.state, {
                options: this.options
              })
            : keyboardState.onArrowDown(this.state)
        break
      case 'ArrowUp':
        nextState = keyboardState.onArrowUp(this.state)
        break
      case 'Enter':
        nextState = keyboardState.onEnter(this.state)
        break
      case 'Escape':
        nextState = keyboardState.onEscape(this.state)
        break
      default:
        nextState = keyboardState.onUnhandledKey(this.state)
    }

    const { result } = nextState

    // preventDefault() only when this component actually did something with
    // the key. Unhandled keys (Home, End, ...) keep their native behaviour,
    // Enter with nothing highlighted submits the form (handleSubmit still
    // blocks text that isn't an option), and Escape with nothing to close
    // or clear reaches an enclosing dialog.
    if (result.type !== 'unhandled' && result.type !== 'noop') {
      event.preventDefault()
    }

    // Unhandled means nothing changed - skip the render. Printable keys hit
    // this path too (they're not in the switch above), and the input's
    // own 'input' event already re-filters and re-renders right after.
    if (result.type !== 'unhandled') {
      this.applyResult(nextState)
    }
  }

  // Runs after the browser has restored the form's default values - 'reset'
  // fires before it does - and puts the component back as it loaded.
  handleReset(event) {
    setTimeout(() => {
      // A consumer cancelled the reset, so no values were restored either
      if (event.defaultPrevented) {
        return
      }
      this.state = keyboardState.createInitialState()
      this.lastChangeValue = null
      this.announceImmediately('')
      if (this.initialErrorHTML) {
        this.$error.hidden = false
        this.$error.innerHTML = this.initialErrorHTML
        this.setErrorState(true)
      } else {
        this.clearError()
      }
      this.render()
    })
  }

  // Disabled (including inside a disabled fieldset) or read-only - the
  // value can't change, so nothing in this component may change it either.
  isLocked() {
    return this.$input.matches(':disabled') || this.$input.readOnly
  }

  // Runs when focus leaves the input or the clear button. Moving between
  // those two doesn't count as leaving (tech approach: "Moving to the clear
  // button doesn't count as leaving") - relatedTarget covers that.
  handleFocusLeave(event) {
    if (
      event.relatedTarget === this.$input ||
      event.relatedTarget === this.$clearButton
    ) {
      return
    }
    // Leaving the component entirely should close the popup, same as the
    // first Escape press - otherwise it's left open and aria-expanded stays
    // "true" on a control that no longer has focus.
    if (this.state.isOpen) {
      this.state = keyboardState.onEscape(this.state)
      this.render()
    }
    // Cancels any pending result count, which would otherwise be read out
    // after focus has already moved to another field.
    this.announceImmediately('')
    this.validateNotInList({ moveFocus: false })
  }

  handleClear() {
    if (this.isLocked()) {
      return
    }
    this.$input.value = ''
    this.dispatchChange()
    this.state = keyboardState.onType(this.state, {
      query: '',
      options: this.options,
      mode: this.match,
      minChars: this.minChars
    })
    this.clearError()
    // Before render(), which hides Clear now the value is empty - focus
    // would otherwise drop to <body>, and the clear button's blur would see
    // no relatedTarget and treat it as leaving the component.
    this.$input.focus()
    this.render()
    this.announceImmediately('')
  }

  handleToggleClick(event) {
    if (this.isLocked()) {
      return
    }
    this.state = keyboardState.onRequestFullList(this.state, {
      options: this.options,
      // A real mouse click (event.detail >= 1) opens the list with focus
      // staying on the input, nothing pre-highlighted. Enter/Space on the
      // focused button also fires 'click', with event.detail === 0 - that
      // case keeps the same "highlight first" behaviour as ArrowDown, since
      // the user is already keyboard-driving and landing on the first
      // option is the expected, lower-friction continuation.
      highlightFirst: event.detail === 0
    })
    this.render()
    // Safari doesn't focus a clicked <button> by default - without this,
    // the arrow keys the user presses next wouldn't reach this component.
    this.$input.focus()
  }

  handleOptionClick(event) {
    if (this.isLocked()) {
      return
    }
    const $option = event.target.closest('.hse-combobox__option')
    if (!$option) {
      return
    }
    const option = this.options.find((candidate) => candidate.id === $option.id)
    // The list can still be open with focus on Clear (moving to Clear
    // doesn't close it), and the listbox's mousedown keeps focus wherever it
    // was - so bring it back to the input, where the arrow keys work. Before
    // applyResult, so the focus readout doesn't cut off "X selected".
    if (document.activeElement !== this.$input) {
      this.$input.focus()
    }
    this.applyResult(keyboardState.onOptionClick(this.state, { option }))
  }

  handleSubmit(event) {
    // A locked value can't be fixed by the user (and a disabled one isn't
    // even submitted), so blocking the form on it would trap them.
    if (this.isLocked()) {
      return
    }
    const value = this.$input.value
    // Empty is left to the service's own required check, and without an
    // errorNotInList message there's no feedback to give - blocking either
    // would leave the form unsubmittable with nothing on screen to say why.
    if (
      this.context === 'search' ||
      value.length === 0 ||
      !this.errorNotInList ||
      this.acceptExactMatch(value)
    ) {
      return
    }
    // Another combobox earlier in the same form already blocked this submit
    // and took focus - show this one's error but leave focus on the first.
    const moveFocus = !event.defaultPrevented
    event.preventDefault()
    this.validateNotInList({ moveFocus })
  }

  // Applies a transition's result (not just its state) - confirmations and
  // closes need side effects (writing the input value, announcing) beyond
  // what render() alone does from state.
  applyResult(nextState) {
    const { result } = nextState
    this.state = nextState

    if (result.type === 'confirmed') {
      this.$input.value = result.option.label
      this.clearError()
    } else if (result.type === 'cleared') {
      // Second Escape after the list was dismissed - keyboard-state.js
      // already decided the value should clear, but writing it to the
      // input is this adapter's job, same as the 'confirmed' branch above.
      this.$input.value = ''
      this.clearError()
    }

    if (result.type === 'confirmed' || result.type === 'cleared') {
      this.dispatchChange()
    }

    this.render()

    if (result.type === 'confirmed') {
      this.announceImmediately(
        this.selectedText.replace('%{option}', result.option.label)
      )
    } else if (result.type === 'highlighted') {
      this.announceResultCount()
    } else if (result.type === 'closed' || result.type === 'cleared') {
      // Nothing to count once the list is closed or the text is gone -
      // empties the region and cancels any count still pending.
      this.announceImmediately('')
    }
  }

  // Setting .value from script fires no events, so consumers listening for
  // 'change' would never see a selection or a clear. Not 'input' - that would
  // re-run handleInput and re-open the list filtered to the chosen label.
  // Records the value so the browser's own 'change' on the next blur, for
  // this same value, can be dropped as a repeat (see bindEvents).
  dispatchChange() {
    this.lastChangeValue = this.$input.value
    this.$input.dispatchEvent(new Event('change', { bubbles: true }))
  }

  render() {
    const { state } = this
    const isOpen = state.isOpen

    const isListboxShown = isOpen && state.status === 'hasMatches'

    // Tracks the listbox itself, not just "open" - with no matches only the
    // aria-hidden "No results found" copy shows, so "expanded" would point a
    // screen reader at a listbox it can't reach. The status region announces
    // the no-results case instead.
    this.$input.setAttribute('aria-expanded', String(isListboxShown))

    if (state.highlightedId) {
      this.$input.setAttribute('aria-activedescendant', state.highlightedId)
    } else {
      this.$input.removeAttribute('aria-activedescendant')
    }

    const matchedIds = new Set(state.matches.map((option) => option.id))
    this.$optionElements.forEach(($option) => {
      const isMatch = matchedIds.has($option.id)
      $option.hidden = !isMatch
      const isHighlighted = $option.id === state.highlightedId
      if (isHighlighted) {
        $option.setAttribute('aria-selected', 'true')
      } else {
        $option.removeAttribute('aria-selected')
      }
    })

    this.$listbox.hidden = !isListboxShown
    if (this.$noResults) {
      this.$noResults.hidden = !(isOpen && state.status === 'noMatches')
    }

    if (this.$clearButton) {
      this.$clearButton.hidden = this.$input.value.length === 0
    }

    // Before scrolling, so the page only ever scrolls to where the popup
    // finally sits - scrolling first could move the page to show an option
    // below the field, then flip the popup above it.
    if (isOpen) {
      this.updatePosition()
    }

    // Only once the listbox and its options are shown - a hidden element
    // measures as zero, so scrolling any earlier (for example re-opening
    // with Up Arrow straight onto the last option) would leave the
    // highlighted option out of view.
    if (isListboxShown && state.highlightedId) {
      this.scrollOptionIntoView(document.getElementById(state.highlightedId))
    }
  }

  // block: 'nearest' scrolls the listbox first and moves the page only as
  // far as it must - so a list that runs off screen (one that fits neither
  // below nor above the field) still brings the highlighted option into
  // view, and a consuming site's scroll-padding for a sticky header is
  // respected. Not implemented by jsdom, hence the guard.
  scrollOptionIntoView($option) {
    if (typeof $option.scrollIntoView === 'function') {
      $option.scrollIntoView({ block: 'nearest', inline: 'nearest' })
    }
  }

  // Decides whether to flip the popup above the field, then leaves the
  // actual positioning to the `hse-combobox--above` CSS rule - no pixel
  // measurement needed, since the field is the popup's only in-flow sibling.
  updatePosition() {
    if (!this.state.isOpen) {
      return
    }
    const fieldRect = this.$field.getBoundingClientRect()
    const spaceBelow = window.innerHeight - fieldRect.bottom
    const spaceAbove = fieldRect.top
    // Measures whichever popup is showing (a filtered list is often much
    // shorter than its max-height). Flips only when it doesn't fit below but
    // does fit above - flipping a popup that fits neither way would push its
    // top off the page, where nobody can scroll to it.
    const $popup = this.$listbox.hidden ? this.$noResults : this.$listbox
    const popupHeight = $popup ? $popup.offsetHeight : 0
    const shouldFlip = spaceBelow < popupHeight && spaceAbove >= popupHeight
    this.$module.classList[shouldFlip ? 'add' : 'remove']('hse-combobox--above')
  }

  findExactMatch(value) {
    const normalizedValue = normalize(value)
    return this.options.find(
      (option) => normalize(option.label) === normalizedValue
    )
  }

  // Typing an option's text counts as choosing it, even with different
  // case, spacing or accents - so the input takes that option's own text,
  // which is what the form submits ("car diology" -> "Cardiology").
  acceptExactMatch(value) {
    const option = this.findExactMatch(value)
    if (!option) {
      return false
    }
    // Confirmed like a list selection - the list closes (Enter can submit
    // with it open, and a page that handles submit itself stays put), and a
    // later single Escape can't wipe the value.
    this.state = keyboardState.onAcceptMatch(this.state, { option })
    if (option.label !== value) {
      this.$input.value = option.label
      this.dispatchChange()
    }
    this.render()
    // Nothing left to count once the list is closed
    this.announceImmediately('')
    return true
  }

  validateNotInList({ moveFocus }) {
    const value = this.$input.value
    // Search takes free text by design, so there's no "not in list" for it.
    if (
      this.isLocked() ||
      this.context === 'search' ||
      value.length === 0 ||
      !this.errorNotInList
    ) {
      return
    }
    // Choosing an option by typing its text removes the error the same way
    // picking it from the list would.
    if (this.acceptExactMatch(value)) {
      this.clearError()
      return
    }
    this.showError(this.errorNotInList)
    if (moveFocus && document.activeElement !== this.$input) {
      // Focus lands on the input, whose aria-describedby and aria-invalid
      // already read the error - announcing it as well would say it twice.
      this.$input.focus()
    } else {
      // Either focus has moved on to another field, or it was already in
      // this input (Enter submitted the form), so focus() changes nothing -
      // in both cases the description won't be read again, and the status
      // region is the only way to hear the error. Prefixed to match the
      // error message's own visually hidden prefix.
      this.announceImmediately(
        `${this.errorPrefix}: ${this.getLabelText()}: ${this.errorNotInList}`
      )
    }
  }

  // Names the field in announcements made when focus may be elsewhere, so
  // with two comboboxes in one form the user can tell which error is which.
  getLabelText() {
    const $label = this.$module.querySelector('.hse-label')
    return $label ? $label.textContent.trim() : ''
  }

  showError(message) {
    if (this.$error) {
      this.$error.hidden = false
      // Built here rather than relying on template-rendered markup - the
      // template leaves this span empty when there's no error (see
      // template.njk), since aria-describedby exposes a hidden element's
      // text to screen readers regardless of the `hidden` attribute.
      const $prefix = document.createElement('span')
      $prefix.className = 'hse-u-visually-hidden'
      $prefix.textContent = `${this.errorPrefix}:`
      this.$error.textContent = ''
      this.$error.appendChild($prefix)
      this.$error.appendChild(document.createTextNode(` ${message}`))
    }
    this.setErrorState(true)
  }

  clearError() {
    if (this.$error && !this.$error.hidden) {
      this.$error.hidden = true
      // Emptied, not just hidden - otherwise the previous message stays in
      // the DOM and keeps being read out via aria-describedby (which
      // ignores `hidden`) on every future focus.
      this.$error.textContent = ''
      this.setErrorState(false)
    }
  }

  // Keeps the error styling (form-group bar, red field border) in step with
  // the message, matching what the template renders for a server-side error.
  setErrorState(hasError) {
    // add/remove rather than toggle(name, force), which Safari 12 lacks.
    const method = hasError ? 'add' : 'remove'
    this.$module.classList[method]('hse-combobox--error')
    if (this.$formGroup) {
      this.$formGroup.classList[method]('hse-form-group--error')
    }
    if (hasError) {
      this.$input.setAttribute('aria-invalid', 'true')
    } else {
      this.$input.removeAttribute('aria-invalid')
    }
  }

  getResultMessage() {
    const { status, matchCount } = this.state
    if (status === 'noMatches') {
      return this.$noResults
        ? this.$noResults.textContent.trim()
        : 'No results found'
    }
    if (status === 'hasMatches') {
      const template =
        matchCount === 1 ? this.resultsOneText : this.resultsOtherText
      return template.replace('%{count}', matchCount)
    }
    return ''
  }

  announceResultCount() {
    clearTimeout(this.announceTimer)
    this.announceTimer = setTimeout(() => {
      this.writeStatus(this.getResultMessage())
    }, ANNOUNCE_DEBOUNCE_MS)
  }

  announceImmediately(message) {
    clearTimeout(this.announceTimer)
    this.writeStatus(message)
  }

  writeStatus(message) {
    if (this.$status) {
      this.$status.textContent = message
    }
  }
}

module.exports = ({ scope = document } = {}) => {
  // Browsers without these (IE11) keep the plain text input instead. Without
  // the check, Array.from throws in init() and stops every component after
  // this one in initAll() from initialising.
  if (
    typeof Array.from !== 'function' ||
    typeof String.prototype.normalize !== 'function'
  ) {
    return
  }

  const comboboxes = scope.querySelectorAll('[data-module="hse-combobox"]')
  comboboxes.forEach(($module) => {
    // initAll(scope) can run again over content it has already seen - a
    // second instance would bind every listener twice.
    if ($module.dataset.comboboxInitialised) {
      return
    }
    $module.dataset.comboboxInitialised = 'true'
    new Combobox($module).init()
  })
}

module.exports.Combobox = Combobox
